import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import adminService from '../../services/adminService';
import { useToast } from '../../context/ToastContext';
import socketService from '../../services/socket';
import AdminLayout from '../../components/admin/AdminLayout';

const DOC_TYPES = [
    { key: 'identityDocument', label: 'Identity Document' },
    { key: 'dbsCertificate', label: 'DBS Certificate' },
    { key: 'safeguardingCourse', label: 'Safeguarding Course' },
];

const FALLBACK_DOCS = [
    { key: 'drivingLicense', label: 'Driving License' },
    { key: 'arc', label: 'ARC' },
    { key: 'passportFront', label: 'Passport Front' },
    { key: 'bankStatement', label: 'Bank Statement' },
];

const PAManagement = () => {
    const [pas, setPAs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPA, setSelectedPA] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [issueDate, setIssueDate] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // all | verified | expiring | expired
    const [actionLoading, setActionLoading] = useState(false);
    const [reviewingDoc, setReviewingDoc] = useState(null);
    const [imgError, setImgError] = useState(false);
    
    const toast = useToast();

    // ── Ref to always read latest selectedPA inside stable socket callbacks ──
    const selectedPARef = React.useRef(null);
    React.useEffect(() => { selectedPARef.current = selectedPA; }, [selectedPA]);

    const fetchPAs = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/admin/pas');
            const freshList = data.data.pas;
            setPAs(freshList);
            // If a PA is currently selected, sync their data with the freshly fetched version
            if (selectedPARef.current) {
                const updated = freshList.find(p => p._id === selectedPARef.current._id);
                if (updated) setSelectedPA(updated);
            }
        } catch (error) {
            toast.error('Failed to load Passenger Assistants');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPAs();

        // pa:doc:updated — sent by admin when verifying docs;
        // use functional updater + ref so we don't need selectedPA in deps
        const handleDocUpdated = (updatedPA) => {
            if (updatedPA && updatedPA._id) {
                setPAs(prev => prev.map(p => p._id === updatedPA._id ? updatedPA : p));
                if (selectedPARef.current && selectedPARef.current._id === updatedPA._id) {
                    setSelectedPA(updatedPA);
                }
            }
        };

        // pa:doc:uploaded / pa:registered — full list refresh when PA acts
        const handlePARefresh = () => fetchPAs();

        socketService.on('pa:doc:updated', handleDocUpdated);
        socketService.on('pa:registered', handlePARefresh);
        socketService.on('pa:doc:uploaded', handlePARefresh);

        return () => {
            socketService.off('pa:doc:updated', handleDocUpdated);
            socketService.off('pa:registered', handlePARefresh);
            socketService.off('pa:doc:uploaded', handlePARefresh);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // ← run ONCE on mount; refs handle current selectedPA

    const [rejectModal, setRejectModal] = useState({ open: false, docType: '', isFallback: false });
    const [rejectReason, setRejectReason] = useState('');

    const handleVerifyDoc = async (docType, status, isFallback = false) => {
        if (status === 'rejected') {
            setRejectModal({ open: true, docType, isFallback });
            return;
        }
        await submitVerification(docType, status, isFallback);
    };

    const submitVerification = async (docType, status, isFallback = false, reason = '') => {
        try {
            setActionLoading(true);
            const key = isFallback ? `fallbackDocuments.${docType}` : docType;
            let payload = { type: key, status };
            if (reason) payload.reason = reason;

            const { data } = await api.patch(`/admin/pas/${selectedPA._id}/verify-document`, payload);
            toast.success(`Document ${status} successfully`);
            setSelectedPA(data.data.pa);
            setPAs(prev => prev.map(p => p._id === data.data.pa._id ? data.data.pa : p));
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update document status');
        } finally {
            setActionLoading(false);
        }
    };

    const [deleteModal, setDeleteModal] = useState({ open: false, docType: '', isFallback: false });

    const handleDeletePADocConfirm = async () => {
        const { docType, isFallback } = deleteModal;
        try {
            setActionLoading(true);
            setDeleteModal({ open: false, docType: '', isFallback: false });
            const key = isFallback ? `fallbackDocuments.${docType}` : docType;
            const { data } = await adminService.deletePADocument(selectedPA._id, key);
            toast.success("Document deleted successfully");
            setSelectedPA(data.data.pa);
            setPAs(prev => prev.map(p => p._id === data.data.pa._id ? data.data.pa : p));
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete document');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeletePADoc = (docType, isFallback = false) => {
        setDeleteModal({ open: true, docType, isFallback });
    };

    // ── Permanently Delete PA ──
    const [deletePAModal, setDeletePAModal] = useState({ open: false, pa: null });
    const [deletePALoading, setDeletePALoading] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    const handleDeletePAConfirm = async () => {
        if (!deletePAModal.pa) return;
        setDeletePALoading(true);
        try {
            await adminService.deletePA(deletePAModal.pa._id);
            toast.success(`PA "${deletePAModal.pa.name}" permanently deleted`);
            setDeletePAModal({ open: false, pa: null });
            setDeleteConfirmText('');
            setSelectedPA(null);
            fetchPAs();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete PA');
        } finally {
            setDeletePALoading(false);
        }
    };

    const handleRejectConfirm = () => {
        if (!rejectReason.trim()) return;
        submitVerification(rejectModal.docType, 'rejected', rejectModal.isFallback, rejectReason);
        setRejectModal({ open: false, docType: '', isFallback: false });
        setRejectReason('');
    };

    const handleUploadHomeOffice = async (e) => {
        e.preventDefault();
        if (!uploadFile) return toast.error('Please select a file');

        try {
            setActionLoading(true);
            const formData = new FormData();
            formData.append('document', uploadFile);
            formData.append('type', 'homeOfficeDocument');
            if (issueDate) formData.append('issueDate', issueDate);
            if (expiryDate) formData.append('expiryDate', expiryDate);

            const { data } = await api.post(`/admin/pas/${selectedPA._id}/upload-document`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Home Office Document uploaded successfully');
            setSelectedPA(data.data.pa);
            setPAs(prev => prev.map(p => p._id === data.data.pa._id ? data.data.pa : p));
            setUploadFile(null);
            setIssueDate('');
            setExpiryDate('');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to upload document');
        } finally {
            setActionLoading(false);
        }
    };

    const getDocUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        if (url.startsWith('/uploads/')) return '/api' + url;
        return url;
    };

    const renderDocumentStatus = (docObj, docType, isFallback = false) => {
        if (!docObj || !docObj.url) return <span className="text-slate-500 italic text-sm">Not Uploaded</span>;

        const docLabels = {
            identityDocument: 'Identity Document',
            dbsCertificate: 'DBS Certificate',
            safeguardingCourse: 'Safeguarding Course',
            drivingLicense: 'Driving License',
            arc: 'ARC',
            passportFront: 'Passport Front',
            bankStatement: 'Bank Statement',
        };

        return (
            <div className="flex flex-col gap-2 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => {
                            setImgError(false);
                            setReviewingDoc({
                                paName: selectedPA?.name,
                                type: docType,
                                label: docLabels[docType] || docType,
                                url: docObj.url,
                                status: docObj.status,
                                expiryDate: docObj.expiryDate,
                                reason: docObj.rejectionReason,
                                isFallback,
                            });
                        }}
                        className="text-indigo-600 dark:text-amber-500 hover:underline text-sm font-bold flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        Review Document
                    </button>
                    {(() => {
                        const isExpired = docObj.expiryDate && new Date(docObj.expiryDate) < new Date();
                        const isExpiringSoon = docObj.expiryDate && new Date(docObj.expiryDate) > new Date() && (new Date(docObj.expiryDate) - new Date()) / (1000 * 60 * 60 * 24) <= 30;
                        
                        let badgeStyle = '';
                        let badgeText = '';

                        if (isExpired) {
                            badgeStyle = 'bg-rose-500/10 text-rose-600 border-rose-500/30 shadow-[0_0_15px_rgba(244,63,94,0.3)] animate-pulse';
                            badgeText = '⚠ EXPIRED';
                        } else if (isExpiringSoon && docObj.status === 'approved') {
                            badgeStyle = 'bg-amber-500/10 text-amber-600 border-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.2)] animate-pulse';
                            badgeText = 'EXPIRING SOON';
                        } else if (docObj.status === 'approved') {
                            badgeStyle = 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20';
                            badgeText = '✓ APPROVED';
                        } else if (docObj.status === 'rejected') {
                            badgeStyle = 'bg-rose-500/10 text-rose-700 border-rose-500/20';
                            badgeText = 'REJECTED';
                        } else {
                            badgeStyle = 'bg-amber-500/10 text-amber-700 border-amber-500/20';
                            badgeText = 'PENDING';
                        }

                        return (
                            <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest rounded-md border ${badgeStyle}`}>
                                {badgeText}
                            </span>
                        );
                    })()}
                </div>
                
                {docObj.url && (
                    <div className="flex gap-2 mt-2">
                        {docObj.status === 'pending' && (
                            <>
                                <button onClick={() => handleVerifyDoc(docType, 'approved', isFallback)} disabled={actionLoading} className="flex-1 py-1.5 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-lg text-xs font-bold hover:from-emerald-600 hover:to-teal-700 transition-colors shadow-sm"><svg className="w-3 h-3 mr-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> Approve</button>
                                <button onClick={() => handleVerifyDoc(docType, 'rejected', isFallback)} disabled={actionLoading} className="flex-1 py-1.5 bg-gradient-to-br from-rose-500 to-red-600 text-white rounded-lg text-xs font-bold hover:from-rose-600 hover:to-red-700 transition-colors shadow-sm"><svg className="w-3 h-3 mr-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg> Reject</button>
                            </>
                        )}
                        <button onClick={() => handleDeletePADoc(docType, isFallback)} disabled={actionLoading} className="flex-1 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"><svg className="w-3 h-3 mr-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg> Delete</button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <AdminLayout title="Passenger Assistants" subtitle="Manage PA compliance and documents" hideSearch>
            <div className="max-w-7xl mx-auto">

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left side: PA List */}
                <div className="lg:col-span-1 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-4">
                    <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-4">Registered PAs</h2>

                    {/* Search & Filters */}
                    <div className="space-y-3 mb-4">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            </div>
                            <input 
                                type="text" 
                                placeholder="Search PAs..." 
                                value={search} 
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/60 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:text-white placeholder:text-slate-400"
                            />
                        </div>
                        <div className="flex gap-2 flex-nowrap overflow-x-auto scrollbar-hide pb-1 max-w-full min-w-0 snap-x">
                            <button onClick={() => setFilter('all')} className={`shrink-0 snap-start px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-colors ${filter === 'all' ? 'bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'}`}>All</button>
                            <button onClick={() => setFilter('verified')} className={`shrink-0 snap-start px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-colors flex items-center gap-1 ${filter === 'verified' ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/20' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/20'}`}>✓ Verified</button>
                            <button onClick={() => setFilter('expiring')} className={`shrink-0 snap-start px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-colors flex items-center gap-1 ${filter === 'expiring' ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/20' : 'bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:hover:bg-amber-500/20'}`}><svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Expiring</button>
                            <button onClick={() => setFilter('expired')} className={`shrink-0 snap-start px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-colors flex items-center gap-1 ${filter === 'expired' ? 'bg-rose-500 text-white shadow-sm shadow-rose-500/20' : 'bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:hover:bg-rose-500/20'}`}>⚠ Expired</button>
                        </div>
                    </div>
                    
                    {loading ? (
                        <div className="animate-pulse space-y-3">
                            {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-100 dark:bg-slate-800 rounded-xl" />)}
                        </div>
                    ) : (() => {
                        const isPAExpired = (pa) => {
                            const docs = pa.paDocuments || {};
                            return Object.values(docs).some(doc => doc?.expiryDate && new Date(doc.expiryDate) < new Date());
                        };
                        const isPAExpiringSoon = (pa) => {
                            const docs = pa.paDocuments || {};
                            return !isPAExpired(pa) && Object.values(docs).some(doc => doc?.expiryDate && new Date(doc.expiryDate) > new Date() && (new Date(doc.expiryDate) - new Date()) / (1000 * 60 * 60 * 24) <= 30);
                        };
                        const isPAVerified = (pa) => pa.isVerified && !isPAExpired(pa);

                        const filtered = pas.filter(pa => {
                            const matchesSearch = pa.name?.toLowerCase().includes(search.toLowerCase()) || pa.email?.toLowerCase().includes(search.toLowerCase());
                            if (!matchesSearch) return false;
                            switch (filter) {
                                case 'verified': return isPAVerified(pa);
                                case 'expiring': return isPAExpiringSoon(pa);
                                case 'expired': return isPAExpired(pa);
                                default: return true;
                            }
                        });

                        if (filtered.length === 0) {
                            return <p className="text-slate-500 text-sm text-center py-4">No PAs found matching criteria.</p>;
                        }

                        return (
                            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                                {filtered.map(pa => {
                                    const hasExpired = isPAExpired(pa);
                                    const hasExpiringSoon = isPAExpiringSoon(pa);
                                
                                return (
                                    <div
                                        key={pa._id}
                                        className={`w-full text-left p-3 rounded-xl border transition-all ${
                                            selectedPA?._id === pa._id 
                                                ? 'bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/30 shadow-sm' 
                                                : hasExpired 
                                                    ? 'border-rose-200 dark:border-rose-500/30 bg-rose-50/50 dark:bg-rose-500/5 hover:border-rose-300' 
                                                    : 'border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
                                        } flex items-center justify-between gap-2`}
                                    >
                                        <button onClick={() => setSelectedPA(pa)} className="flex-1 text-left">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="font-bold text-slate-800 dark:text-white text-sm hover:text-indigo-600 transition-colors">{pa.name}</p>
                                                    <p className="text-xs text-slate-500 truncate max-w-[150px] sm:max-w-[200px]">{pa.email}</p>
                                                </div>
                                                <div className="flex flex-col items-end gap-1">
                                                    {hasExpired ? (
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-rose-500/10 text-rose-600 border border-rose-500/30 shadow-[0_0_15px_rgba(244,63,94,0.3)] animate-pulse">
                                                            ⚠ EXPIRED
                                                        </span>
                                                    ) : hasExpiringSoon ? (
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-amber-500/10 text-amber-600 border border-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.2)] animate-pulse">
                                                            <span className="flex items-center gap-1"><svg className="w-3.5 h-3.5 flex-shrink-0 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> EXPIRING</span>
                                                        </span>
                                                    ) : (
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${pa.isVerified ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-yellow-100 text-yellow-700 border border-yellow-200'}`}>
                                                            {pa.isVerified ? 'Verified' : 'Pending'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                        
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); setDeletePAModal({ open: true, pa }); }}
                                            title="Permanently Delete PA"
                                            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors shrink-0 border border-transparent hover:border-rose-500/20"
                                        >
                                            <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                        );
                    })()}
                </div>

                {/* Right side: Details & Documents */}
                <div className="lg:col-span-2">
                    {selectedPA ? (
                        <div className="space-y-6">
                            
                            {/* Primary Documents */}
                            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                                <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/20">
                                    <h3 className="font-bold text-slate-800 dark:text-white">Primary Documents</h3>
                                </div>
                                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {DOC_TYPES.map(doc => (
                                        <div key={doc.key}>
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">{doc.label}</label>
                                            {renderDocumentStatus(selectedPA.paDocuments?.[doc.key], doc.key)}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Fallback Documents */}
                            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                                <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/20">
                                    <h3 className="font-bold text-slate-800 dark:text-white">Fallback Documents</h3>
                                </div>
                                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {FALLBACK_DOCS.map(doc => (
                                        <div key={doc.key}>
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">{doc.label}</label>
                                            {renderDocumentStatus(selectedPA.paDocuments?.fallbackDocuments?.[doc.key], doc.key, true)}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Home Office Document Upload (Admin Only) */}
                            <div className="bg-indigo-50 dark:bg-indigo-500/5 rounded-2xl border border-indigo-100 dark:border-indigo-500/20 overflow-hidden shadow-sm">
                                <div className="p-5 border-b border-indigo-100 dark:border-indigo-500/20 bg-indigo-100/50 dark:bg-indigo-500/10">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-indigo-500 text-white flex items-center justify-center">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-indigo-900 dark:text-indigo-400">Home Office Document</h3>
                                            <p className="text-xs text-indigo-700/70 dark:text-indigo-500/70">Secure upload • Hidden from PA</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="p-5">
                                    {selectedPA.paDocuments?.homeOfficeDocument?.url ? (
                                        <div className="mb-6 p-4 bg-white dark:bg-slate-900 rounded-xl border border-indigo-100 dark:border-indigo-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">✓</div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-800 dark:text-white">Document Active</p>
                                                    <button onClick={() => { setImgError(false); setReviewingDoc({ paName: selectedPA?.name, type: 'homeOfficeDocument', label: 'Home Office Document', url: selectedPA.paDocuments.homeOfficeDocument.url, status: 'approved', expiryDate: selectedPA.paDocuments.homeOfficeDocument.expiryDate, reason: null, isFallback: false }); }} className="text-indigo-500 hover:underline text-xs font-bold">View File</button>
                                                </div>
                                            </div>
                                            <div className="flex gap-4">
                                                <div>
                                                    <p className="text-[10px] font-black uppercase text-slate-400">Issue Date</p>
                                                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300">{selectedPA.paDocuments.homeOfficeDocument.issueDate ? new Date(selectedPA.paDocuments.homeOfficeDocument.issueDate).toLocaleDateString() : 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-black uppercase text-slate-400">Expiry Date</p>
                                                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300">{selectedPA.paDocuments.homeOfficeDocument.expiryDate ? new Date(selectedPA.paDocuments.homeOfficeDocument.expiryDate).toLocaleDateString() : 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}

                                    <form onSubmit={handleUploadHomeOffice} className="space-y-4 bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800">
                                        <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-2">{selectedPA.paDocuments?.homeOfficeDocument?.url ? 'Upload Replacement Document' : 'Upload New Document'}</h4>
                                        
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 mb-1">Issue Date</label>
                                                <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} required className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:border-indigo-500" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 mb-1">Expiry Date</label>
                                                <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} required className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:border-indigo-500" />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 mb-1">Select File (PDF/Image)</label>
                                            <input type="file" accept="image/*,application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={e => setUploadFile(e.target.files[0])} required className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
                                        </div>

                                        <button type="submit" disabled={actionLoading || !uploadFile} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm font-bold transition-colors shadow-sm">
                                            {actionLoading ? 'Uploading...' : 'Upload & Save Home Office Document'}
                                        </button>
                                    </form>
                                </div>
                            </div>

                        </div>
                    ) : (
                        <div className="h-full min-h-[400px] flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 border-dashed">
                            <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mb-4 text-indigo-500">
                                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>
                            </div>
                            <p className="text-slate-500 font-medium">Select a Passenger Assistant to manage documents</p>
                        </div>
                    )}
                </div>
            </div>
            </div>

            {/* Rejection Reason Modal */}
            {rejectModal.open && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => { setRejectModal({ open: false, docType: '', isFallback: false }); setRejectReason(''); }}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-black text-slate-800 dark:text-white mb-2">Reject Document</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Please provide a reason for rejecting this document.</p>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Enter rejection reason..."
                            rows={3}
                            autoFocus
                            className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-800 dark:text-white outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all resize-none"
                        />
                        <div className="flex gap-3 mt-4">
                            <button
                                onClick={() => { setRejectModal({ open: false, docType: '', isFallback: false }); setRejectReason(''); }}
                                className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            >Cancel</button>
                            <button
                                onClick={handleRejectConfirm}
                                disabled={!rejectReason.trim() || actionLoading}
                                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >{actionLoading ? 'Rejecting...' : 'Reject'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Document Preview Modal (matches admin DocumentManagement pattern) */}
            {reviewingDoc && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-md">
                    <div className="bg-slate-50 dark:bg-slate-900 w-full max-w-4xl rounded-[2.5rem] shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/20 dark:border-white/5 overflow-hidden flex flex-col max-h-[95vh]">
                        {/* Header */}
                        <div className="p-6 md:p-8 border-b border-slate-200/50 dark:border-slate-800 bg-white/50 dark:bg-black/20 shrink-0 flex justify-between items-center z-10 backdrop-blur-3xl">
                            <div>
                                <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight uppercase">Document Verification</h3>
                                <p className="text-[10px] font-bold text-indigo-500 dark:text-amber-500 tracking-widest uppercase mt-2">PA: {reviewingDoc.paName} • {reviewingDoc.label}</p>
                            </div>
                            <button onClick={() => setReviewingDoc(null)} className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-200/50 dark:bg-slate-800 hover:bg-rose-500/10 text-slate-500 hover:text-rose-500 dark:text-slate-400 transition-colors">
                                <svg fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 overflow-y-auto min-h-0 bg-transparent flex-1">
                            {/* Visual Preview */}
                            <div className="border-r border-slate-200/50 dark:border-slate-800 flex flex-col bg-slate-100/50 dark:bg-black/40 min-h-[400px]">
                                <div className="w-full relative flex items-center justify-center flex-1 p-4 overflow-hidden sidebar-scroll">
                                    {(reviewingDoc.url.toLowerCase().split('?')[0].endsWith('.pdf') || imgError) ? (
                                        <object
                                            data={`${getDocUrl(reviewingDoc.url)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                            type="application/pdf"
                                            className="w-full h-full min-h-[600px] border-0 rounded-2xl"
                                            title="PDF Document"
                                        >
                                            <iframe src={`${getDocUrl(reviewingDoc.url)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} className="w-full h-full min-h-[600px] border-0 rounded-2xl" title="PDF Document" scrolling="no" />
                                        </object>
                                    ) : (
                                        <img
                                            src={getDocUrl(reviewingDoc.url)}
                                            alt="Document Preview"
                                            onError={() => setImgError(true)}
                                            className="w-full h-auto max-h-full object-contain rounded-2xl bg-white/50 dark:bg-black/50 p-2 border border-slate-200/50 dark:border-slate-700/50 shadow-sm"
                                        />
                                    )}
                                </div>
                                <div className="p-4 bg-white/80 dark:bg-slate-900 border-t border-slate-200/50 dark:border-slate-800 flex justify-center shrink-0">
                                    <a href={getDocUrl(reviewingDoc.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest hover:bg-indigo-500/10 dark:hover:bg-indigo-500/20 transition-all border border-slate-200 dark:border-slate-700 w-full justify-center">
                                        <svg fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                                        View Full Document
                                    </a>
                                </div>
                            </div>

                            {/* Auditing Controls */}
                            <div className="p-6 md:p-8 space-y-6 bg-white/50 dark:bg-slate-900/50 flex flex-col justify-center">
                                <div className="p-5 rounded-[1.5rem] bg-slate-100/50 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50 flex items-center justify-between">
                                    <div>
                                        <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2">Status</p>
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest border ${
                                            (reviewingDoc.expiryDate && new Date(reviewingDoc.expiryDate) < new Date()) ? 'bg-rose-500/10 text-rose-600 border-rose-500/20' :
                                            reviewingDoc.status === 'approved' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : 
                                            reviewingDoc.status === 'rejected' ? 'bg-rose-500/10 text-rose-600 border-rose-500/20' : 
                                            'bg-amber-500/10 text-amber-600 border-amber-500/20'
                                        }`}>
                                            {(reviewingDoc.expiryDate && new Date(reviewingDoc.expiryDate) < new Date()) ? 'EXPIRED' : reviewingDoc.status === 'approved' ? 'APPROVED' : reviewingDoc.status === 'rejected' ? 'REJECTED' : 'PENDING'}
                                        </span>
                                    </div>
                                    {reviewingDoc.expiryDate && (
                                        <div className="text-right">
                                            <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2">Expiry Date</p>
                                            <p className="text-xs font-black uppercase tracking-widest px-3 py-1 rounded-md bg-slate-200/50 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                                {new Date(reviewingDoc.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {reviewingDoc.reason && (
                                    <div className="p-5 rounded-[1.5rem] bg-rose-500/5 border border-rose-500/20">
                                        <p className="text-[10px] font-black text-rose-600 dark:text-rose-500 uppercase tracking-widest mb-3">Rejection Reason</p>
                                        <p className="text-sm text-rose-700 dark:text-rose-400 font-bold bg-white/50 dark:bg-black/20 p-4 rounded-xl border border-rose-500/10">{reviewingDoc.reason}</p>
                                    </div>
                                )}

                                <div className="space-y-4 pt-4 border-t border-slate-200/50 dark:border-slate-800">
                                    <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Verification Actions</p>
                                    <div className="grid grid-cols-2 gap-4">
                                        {reviewingDoc.status === 'pending' && (
                                            <>
                                                <button onClick={() => { handleVerifyDoc(reviewingDoc.type, 'approved', reviewingDoc.isFallback); setReviewingDoc(null); }} disabled={actionLoading} className="py-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-[11px] font-black uppercase tracking-widest hover:scale-[1.02] shadow-lg shadow-emerald-500/20 active:scale-95 transition-all disabled:opacity-50">
                                                    {actionLoading ? 'PROCESSING...' : <><svg className="w-3 h-3 mr-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> APPROVE</>}
                                                </button>
                                                <button onClick={() => { setReviewingDoc(null); handleVerifyDoc(reviewingDoc.type, 'rejected', reviewingDoc.isFallback); }} disabled={actionLoading} className="py-4 rounded-2xl bg-gradient-to-br from-rose-500 to-red-600 text-white text-[11px] font-black uppercase tracking-widest hover:scale-[1.02] shadow-lg shadow-rose-500/20 active:scale-95 transition-all disabled:opacity-50">
                                                    {actionLoading ? 'PROCESSING...' : <><svg className="w-3 h-3 mr-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg> REJECT</>}
                                                </button>
                                            </>
                                        )}
                                        <button onClick={() => { handleDeletePADoc(reviewingDoc.type, reviewingDoc.isFallback); setReviewingDoc(null); }} disabled={actionLoading} className="py-4 rounded-2xl bg-gradient-to-br from-slate-600 to-slate-800 text-white text-[11px] font-black uppercase tracking-widest hover:scale-[1.02] shadow-lg shadow-slate-500/20 active:scale-95 transition-all disabled:opacity-50 col-span-2">
                                            {actionLoading ? 'PROCESSING...' : <><svg className="w-3 h-3 mr-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg> DELETE DOCUMENT</>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Delete Document Modal */}
            {deleteModal.open && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
                    <div className="bg-slate-50 dark:bg-slate-900 w-full max-w-sm rounded-[2.5rem] shadow-2xl overflow-hidden border border-white/20 dark:border-white/5 animate-in zoom-in-95 duration-200">
                        <div className="p-6 md:p-8 flex flex-col items-center text-center">
                            <div className="w-16 h-16 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center mb-6">
                                <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                            </div>
                            <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Delete Document?</h3>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-8">
                                Are you sure you want to delete <span className="font-bold text-slate-700 dark:text-slate-300">{deleteModal.docType.replace(/([A-Z])/g, ' $1').toUpperCase()}</span>? The PA will need to re-upload it.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 w-full">
                                <button onClick={() => setDeleteModal({ open: false, docType: '', isFallback: false })} className="flex-1 px-6 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">Cancel</button>
                                <button onClick={handleDeletePADocConfirm} disabled={actionLoading} className="flex-1 px-6 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-white bg-rose-600 hover:bg-rose-700 shadow-xl shadow-rose-500/20 active:scale-95 transition-all disabled:opacity-50">
                                    {actionLoading ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete PA Modal */}
            {deletePAModal.open && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
                    <div className="bg-slate-50 dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden border border-rose-500/20 animate-in zoom-in-95 duration-200">
                        <div className="p-6 md:p-8 flex flex-col items-center text-center">
                            <div className="w-20 h-20 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center mb-6 border-4 border-rose-500/20 shadow-[0_0_15px_rgba(244,63,94,0.3)] animate-pulse">
                                <svg fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-10 h-10"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Delete PA Permanently?</h3>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-4">
                                This will completely remove <span className="font-bold text-rose-600 dark:text-rose-400">{deletePAModal.pa?.name}</span> and all their documents from the system. This action cannot be undone.
                            </p>
                            <div className="w-full bg-rose-50 dark:bg-rose-500/5 border border-rose-100 dark:border-rose-500/10 rounded-xl p-4 mb-6 text-left">
                                <p className="text-xs font-bold text-rose-800 dark:text-rose-300 mb-1">To confirm, type "<span className="text-rose-600 font-black">{deletePAModal.pa?.name}</span>" below:</p>
                                <input 
                                    type="text" 
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                    placeholder="Type PA name to confirm"
                                    className="w-full px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-500/30 text-slate-900 dark:text-white focus:outline-none focus:border-rose-500 mt-2 text-sm"
                                />
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 w-full">
                                <button onClick={() => { setDeletePAModal({ open: false, pa: null }); setDeleteConfirmText(''); }} className="flex-1 px-6 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">Cancel</button>
                                <button 
                                    onClick={handleDeletePAConfirm} 
                                    disabled={deletePALoading || deleteConfirmText !== deletePAModal.pa?.name} 
                                    className="flex-1 px-6 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-white bg-rose-600 hover:bg-rose-700 shadow-xl shadow-rose-500/20 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {deletePALoading ? 'Deleting...' : 'Permanently Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default PAManagement;
